import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { ApiError } from '@/shared/api/client'
import { useAppStore } from '@/shared/stores/appStore'
import {
  addFavoriteLeague,
  fetchUserLeagues,
  joinLeague,
  leagueKeys,
  removeFavoriteLeague,
  updateTeamName,
} from '../queries'
import type { LeagueSummary } from '../types'

export function useUserLeagues() {
  return useQuery({
    queryKey: leagueKeys.list(),
    queryFn:  fetchUserLeagues,
    staleTime: 60_000,
  })
}

export function useToggleFavorite() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ leagueId, currentlyFavorite }: { leagueId: string; currentlyFavorite: boolean }) =>
      currentlyFavorite ? removeFavoriteLeague(leagueId) : addFavoriteLeague(leagueId),

    onMutate: async ({ leagueId, currentlyFavorite }) => {
      await queryClient.cancelQueries({ queryKey: leagueKeys.list() })
      const previous = queryClient.getQueryData<LeagueSummary[]>(leagueKeys.list())
      queryClient.setQueryData<LeagueSummary[]>(leagueKeys.list(), (old) =>
        old?.map((l) => l.id === leagueId ? { ...l, isFavorite: !currentlyFavorite } : l) ?? []
      )
      return { previous }
    },

    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(leagueKeys.list(), context.previous)
      }
    },

    onSettled: () => queryClient.invalidateQueries({ queryKey: leagueKeys.list() }),
  })
}

export function useJoinLeague() {
  const queryClient = useQueryClient()
  const navigate    = useNavigate()
  const addToast    = useAppStore((s) => s.addToast)

  return useMutation({
    mutationFn: ({ code, teamName }: { code: string; teamName?: string | undefined }) =>
      joinLeague(code, teamName),
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({ queryKey: leagueKeys.list() })
      addToast({ type: 'success', title: 'Você entrou na liga!' })
      void navigate({ to: '/league/$leagueId', params: { leagueId: data.leagueId }, search: { tab: 'tabela', gw: undefined } })
    },
    onError: (error: unknown) => {
      if (error instanceof ApiError) {
        // Known business-rule errors (invalid_invite_code, invite_code_not_found,
        // already_member, league_full, league_limit_reached) are surfaced as
        // inline field errors in EmptyState — no toast needed for those.
        // Only show a generic toast for unexpected server errors.
        if (error.status >= 500) {
          addToast({
            type: 'error',
            title: 'Algo deu errado. Não foi possível entrar na liga.',
          })
        }
        return
      }
      throw error
    },
  })
}

export function useUpdateTeamName(leagueId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (teamName: string | null) => updateTeamName(leagueId, teamName),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: leagueKeys.detail(leagueId) })
    },
  })
}
